import { useCookies } from "@vueuse/integrations/useCookies";
import { myContainer } from "@/inversify.config";
import { IEmploymentService } from "@/interfaces/IEmploymentService";
import { TYPES } from "@/types";
import axios from "axios";
import {
  ApplicationUserResponse,
  LoginResponse,
  PermissionResponse,
  PositionResponse,
  UserPositionResponse,
} from "@/models/dtos";
import { UserExportRequest } from "@/models/dtos";
import jwt_decode from "jwt-decode";
import { ContainerUser, NewUser } from "@/models/ContainerUser";
import * as dtos from "@/models/dtos";
import { UserToken } from "@/models/UserToken";

const client = myContainer.get<IEmploymentService>(TYPES.IEmploymentService);

export default {
  state: {
    cookie: localStorage.getItem("user_token"),
    token: {} as LoginResponse,
    user: {} as ContainerUser,
    userToken: {} as UserToken,
    isOdin: null,
    isContainerAdmin: null,
    isContainerWriter: null,
    newUser: {} as NewUser,
  },

  getters: {
    token: (state: any) => state.cookie,
    loggedInUserEmail: (state: any) => {
      return state.token.decoded?.sub;
    },
    userToken: (state: any) => state.userToken,
    loggedInUserIsOdin: (state: any) => state.isOdin,
    loggedInUserIsAdmin: (state: any) => state.isContainerAdmin,
    loggedInUserIsWriter: (state: any) => state.isContainerWriter,
    getLoggedInUser: (state: any, getters: any) => {
      if (
        state?.token?.decoded === null ||
        getters?.container === null ||
        !getters?.container?.users
      )
        return null;
      return getters.container.users.find(
        (x: dtos.UserResponse) => x.email === state.token.decoded?.sub
      ) as dtos.UserResponse;
    },
    getNewUser: (state: any) => state.newUser,
    // ANSKNI-108: external client (Bolag B) detection. The role claim is carried
    // in the JWT issued by /api/account/login, so this is synchronous and safe to
    // use from a router guard. Read-only client; the backend is the real gate.
    isExternalViewer: (state: any) => {
      const role = state?.token?.decoded?.role as string;
      return typeof role === "string" && role.includes("ExternalViewer");
    },
  },

  mutations: {
    setUser(state: any, item: ContainerUser) {
      state.user = item;
    },
    setUserToken: (state: any, token: UserToken) => {
      state.userToken = token;
    },
    setToken: (state: any, token: LoginResponse) => {
      state.token = token;

      if (token === null) {
        return;
      } else {
        const decoded = jwt_decode(token?.token);
        state.token.decoded = decoded;
        client.Container.createClient(token.token);
      }
    },
    decodeToken: (state: any) => {
      const decoded = jwt_decode(state.token?.token);
      state.token.decoded = decoded;
    },
    setIsOdin: (state: any, isOdin: boolean) => {
      state.isOdin = isOdin;
    },
    setIsContainerAdmin: (state: any, isAdmin: boolean) => {
      state.isContainerAdmin = isAdmin;
    },
    setIsContainerWriter: (state: any, isWriter: boolean) => {
      state.isContainerWriter = isWriter;
    },
    setNewUser: (state: any, newUser: NewUser) => {
      state.newUser = newUser;
    },
  },

  actions: {
    initializeUserSession: async ({ commit, getters }: any) => {
      const token = localStorage.getItem("user_token");
      if (token) {
        commit("setToken", { token: token });
        const decoded = jwt_decode<dtos.TokenClaims>(token);
        const userToken = new UserToken(decoded);
        commit("setUserToken", userToken);
      }
    },
    setUser({ commit }: any, item: ContainerUser) {
      commit("setUser", item);
    },
    login: async (
      { commit, dispatch }: any,
      { username, password, remember }: any
    ) => {
      return new Promise((resolve) => {
        client
          .login(username, password)
          .then((response: LoginResponse) => {
            const decoded = jwt_decode<dtos.TokenClaims>(response.token);
            const userToken = new UserToken(decoded);
            commit("setToken", response);
            commit("setUserToken", userToken);
            resolve(userToken);
          })
          .catch((error: any) => {
            resolve(undefined);
          });
      });
    },
    decodeToken({ commit }: any) {
      commit("decodeToken");
    },

    logout: async ({ commit }: any) => {
      commit("setToken", null);
      commit("setContainer", null);
      useCookies(["user_token"]).remove("user_token");
      localStorage.removeItem("user_token");
      localStorage.removeItem("vuex");
    },

    resetContainer: async ({ commit }: any) => {
      commit("setContainer", null);
    },

    reset: async ({ commit }: any, username: string) => {
      return new Promise((resolve) => {
        axios
          .post(TYPES.BaseURL + "/api/account/reset", { email: username })
          .then((response) => {
            resolve("success");
          })
          .catch((error) => {
            // Always report success so the UI never reveals whether the
            // address is registered (account enumeration). The backend now
            // returns 200 for both cases; a catch here means an infra error,
            // which is independent of whether the account exists.
            resolve("success");
          });
      });
    },

    updatePassword: async (
      { commit, getters }: any,
      { password, newPassword, confirmPassword }: any
    ) => {
      return new Promise((resolve, reject) => {
        axios
          .post(
            TYPES.BaseURL + "/api/dispatcher",
            {
              action: "update_password",
              data: {
                password: password,
                newPassword: newPassword,
                confirmNewPassword: confirmPassword,
              },
            },
            { headers: { Authorization: "Bearer " + getters.token } }
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error.response.data);
          });
      });
    },
    getApplicantExport: async ({ commit }: any, payload: UserExportRequest) => {
      const response = await client.Container.getUserExport(payload);

      return response;
    },
    loggedInUserIsOdin: async ({ state, commit }: any) => {
      const role = state?.token?.decoded?.role as string;
      if (typeof role === typeof undefined) {
        commit("setIsOdin", false);
        return false;
      }
      commit("setIsOdin", role.includes("odin"));
      return role.includes("odin");
    },

    loggedInUserIsAdmin: async ({ getters, dispatch, commit }: any) => {
      if (getters.container.users === undefined) {
        await dispatch("init");
      }
      const permissions = getters.getContainerPermissions;
      if (getters.loggedInUserEmail === undefined) {
        await dispatch("decodeToken");
      }

      const userPermissions = getters.container.users.filter(
        (x: ApplicationUserResponse) => x.email === getters.loggedInUserEmail
      );

      const isAdmin = userPermissions.some(
        (user: ApplicationUserResponse) =>
          user.permissionId ===
          permissions.find(
            (p: PermissionResponse) => p.name === "Administrator"
          ).id
      );

      commit("setIsContainerAdmin", isAdmin);
      return isAdmin;
    },
    loggedInUserIsWriter: async ({ getters, dispatch, commit }: any) => {
      if (getters.container.users === undefined) {
        await dispatch("init");
      }

      const permissions = getters.getContainerPermissions;

      if (getters.loggedInUserEmail === undefined) {
        await dispatch("decodeToken");
      }

      const userPermissions = getters.container.users.filter(
        (x: ApplicationUserResponse) => x.email === getters.loggedInUserEmail
      );

      commit(
        "setIsContainerWriter",
        userPermissions.some(
          (user: ApplicationUserResponse) =>
            user.permissionId ===
              permissions.find((p: PermissionResponse) => p.name === "Write")
                .id ||
            user.permissionId ===
              permissions.find(
                (p: PermissionResponse) => p.name === "Administrator"
              ).id
        )
      );

      return getters.loggedInUserIsWriter;
    },

    loggedInUserIsPositionAdmin: async (
      { getters, dispatch }: any,
      positionId: number
    ) => {
      if (getters.loggedInUserIsAdmin === null) {
        await dispatch("loggedInUserIsAdmin");
      }

      if (getters.loggedInUserIsAdmin) return true;

      const position = getters.position as PositionResponse;

      if (position === undefined || position.id !== positionId) {
        await dispatch("loadPosition", positionId);
      }

      const userPositions = getters.position.userPositions;

      const positionAdmins = userPositions.filter(
        (x: UserPositionResponse) => x.permission.name === "Administrator"
      );

      if (getters.loggedInUserEmail === undefined) {
        await dispatch("decodeToken");
      }

      const user = getters.container.users.find(
        (x: ApplicationUserResponse) => x.email === getters.loggedInUserEmail
      );

      return positionAdmins.some(
        (x: UserPositionResponse) => x.user.email === user.email
      );
    },
    loggedInUserIsPositionWriter: async (
      { getters, dispatch }: any,
      positionId: number
    ) => {
      if (getters.loggedInUserIsAdmin === null) {
        await dispatch("loggedInUserIsAdmin");
      }

      if (getters.loggedInUserIsAdmin) return true;

      const position = getters.position as PositionResponse;

      if (position === undefined || position.id !== positionId) {
        await dispatch("loadPosition", positionId);
      }

      const userPositions = getters.position.userPositions;

      const positionWritersAndAdmins = userPositions.filter(
        (x: UserPositionResponse) =>
          x.permission.name === "Write" || x.permission.name === "Administrator"
      );

      if (getters.loggedInUserEmail === undefined) {
        await dispatch("decodeToken");
      }

      const user = getters.container.users.find(
        (x: ApplicationUserResponse) => x.email === getters.loggedInUserEmail
      );

      return positionWritersAndAdmins.some(
        (x: UserPositionResponse) => x.user.email === user.email
      );
    },
    updateNotificationPreferences: async (
      { commit, getters }: any,
      { containerId, notificationPreferences }: any
    ) => {
      return await axios.post(
        TYPES.BaseURL + "/api/dispatcher",
        {
          action: "update_NotificationPreferences",
          data: {
            containerId: containerId,
            notificationPreferences: notificationPreferences,
          },
        },
        { headers: { Authorization: "Bearer " + getters.token } }
      );
    },
    getUserNotificationPreferences: async (
      { commit, getters }: any,
      containerId: number
    ) => {
      const response = await axios.post(
        TYPES.BaseURL + "/api/dispatcher",
        {
          action: "get_UserNotificationPreferences",
          data: {
            containerId: containerId,
          },
        },
        { headers: { Authorization: "Bearer " + getters.token } }
      );
      return response.data;
    },
    async getPositionNotificationPreferences(
      { commit, getters }: any,
      { containerId, positionId }: any
    ) {
      const response = await axios.post(
        TYPES.BaseURL + "/api/dispatcher",
        {
          action: "get_PositionNotificationPreferences",
          data: {
            containerId: containerId,
            positionId: positionId,
          },
        },
        { headers: { Authorization: "Bearer " + getters.token } }
      );
      return response.data;
    },

    async updatePositionNotificationPreferences(
      { commit, getters }: any,
      { containerId, notificationPreferences, positionId }: any
    ) {
      return await axios.post(
        TYPES.BaseURL + "/api/dispatcher",
        {
          action: "update_PositionNotificationPreferences",
          data: {
            containerId: containerId,
            notificationPreferences: notificationPreferences,
            positionId: positionId,
          },
        },
        { headers: { Authorization: "Bearer " + getters.token } }
      );
    },
  },
};
